import { supabase } from '../lib/supabase';

// Get all appointments for authenticated user
export const getUserAppointments = async () => {
  try {
    const { data, error } = await supabase?.from('appointments')?.select(`
        *,
        agent:user_profiles!agent_id(id, full_name, email, phone),
        client:user_profiles!client_id(id, full_name, email, phone),
        property:properties(id, title, address, property_type),
        notary:notaries(id, notary_name, office_address, license_number)
      `)?.order('scheduled_date', { ascending: false });

    if (error) {
      return { data: [], error: error?.message };
    }

    return { data: data || [], error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: [], 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: [], error: 'Failed to fetch appointments' };
  }
};

// Get appointment by ID
export const getAppointmentById = async (id) => {
  try {
    const { data, error } = await supabase?.from('appointments')?.select(`
        *,
        agent:user_profiles!agent_id(id, full_name, email, phone),
        client:user_profiles!client_id(id, full_name, email, phone),
        property:properties(id, title, address, property_type, commune:communes(name)),
        notary:notaries(id, notary_name, office_address, license_number, specializations)
      `)?.eq('id', id)?.single();

    if (error) {
      return { data: null, error: error?.message };
    }

    return { data, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: null, 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: null, error: 'Failed to fetch appointment' };
  }
};

// Get appointments by status
export const getAppointmentsByStatus = async (status) => {
  try {
    const { data, error } = await supabase?.from('appointments')?.select(`
        *,
        agent:user_profiles!agent_id(id, full_name, email, phone),
        client:user_profiles!client_id(id, full_name, email, phone),
        property:properties(id, title, address),
        notary:notaries(id, notary_name, office_address)
      `)?.eq('status', status)?.order('scheduled_date', { ascending: true });

    if (error) {
      return { data: [], error: error?.message };
    }

    return { data: data || [], error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: [], 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: [], error: 'Failed to fetch appointments by status' };
  }
};

// Create new appointment
export const createAppointment = async (appointmentData) => {
  try {
    const { data, error } = await supabase?.from('appointments')?.insert([appointmentData])?.select()?.single();

    if (error) {
      return { data: null, error: error?.message };
    }

    return { data, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: null, 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: null, error: 'Failed to create appointment' };
  }
};

// Update appointment
export const updateAppointment = async (id, updates) => {
  try {
    const { data, error } = await supabase?.from('appointments')?.update(updates)?.eq('id', id)?.select()?.single();

    if (error) {
      return { data: null, error: error?.message };
    }

    return { data, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: null, 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: null, error: 'Failed to update appointment' };
  }
};

// Delete appointment
export const deleteAppointment = async (id) => {
  try {
    const { error } = await supabase?.from('appointments')?.delete()?.eq('id', id);

    if (error) {
      return { error: error?.message };
    }

    return { error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { error: 'Failed to delete appointment' };
  }
};

// Get appointments for a specific date range
export const getAppointmentsByDateRange = async (startDate, endDate) => {
  try {
    const { data, error } = await supabase?.from('appointments')?.select(`
        *,
        agent:user_profiles!agent_id(id, full_name, email, phone),
        client:user_profiles!client_id(id, full_name, email, phone),
        property:properties(id, title, address),
        notary:notaries(id, notary_name, office_address)
      `)?.gte('scheduled_date', startDate)?.lte('scheduled_date', endDate)?.order('scheduled_date', { ascending: true });

    if (error) {
      return { data: [], error: error?.message };
    }

    return { data: data || [], error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: [], 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: [], error: 'Failed to fetch appointments by date range' };
  }
};

export default {
  getUserAppointments,
  getAppointmentById,
  getAppointmentsByStatus,
  createAppointment,
  updateAppointment,
  deleteAppointment,
  getAppointmentsByDateRange
};